import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Accordion,
  Badge,
  Alert,
  Card,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import {
  FaBox,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaRocket,
  FaCouch,
  FaCar,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaMapMarkerAlt,
  FaRoute,
  FaUser,
  FaPhone,
  FaStar,
  FaWeightHanging,
  FaStickyNote,
  FaTruck,
  FaRulerCombined,
  FaUpload,
  FaDownload,
  FaMapMarkedAlt,
  FaCheckCircle,
  FaFileInvoice,
} from "react-icons/fa";
import { driverAPI } from "../../api/apiSystem";
import NavbarMenu from "../heroSection/NavbarMenu";
import { useTranslation } from "react-i18next";
import {
  AlarmIcon,
  DeliverPointIcon,
  LeavePointIcon,
  PackageIcon,
  CalendarIcon,
  ArrowIcon,
} from "../../assets/icons";
import Swal from "sweetalert2";
import { translateBackendMessage } from "../../api/apiSystem";
import { formatNumber, formatDisplayDate } from "../../utils/helpers";
import { RouteMap } from "../RouteMap";
import "../driverTable/PageOne/DarkTheme.css";

const parseDateTime = (orderData = {}, isUnloading = false) => {
  const type = (
    orderData?.transport_type ||
    orderData?.type ||
    ""
  ).toLowerCase();
  const isTwoDates = type === "moving" || type === "car" || type === "cars";

  let date1 = "N/A",
    date2 = "N/A",
    time1 = "N/A",
    time2 = "N/A";

  const propDateTime = isUnloading
    ? orderData.unloading_date_time
    : orderData.loading_date_time;
  const propDate = isUnloading
    ? orderData.unloading_date
    : orderData.loading_date;
  const propTime = isUnloading
    ? orderData.unloading_time
    : orderData.loading_time;

  if (propDateTime?.[0]) {
    const parts = propDateTime[0].split(" ");
    const dStr = parts[0] || "N/A";
    const d = dStr.includes(".") ? dStr.substring(dStr.indexOf(".") + 1) : dStr;
    date1 = d;
    date2 = d;
    time1 = parts[1] || "N/A";
    time2 = parts[3] || "N/A";
  } else if (propDate) {
    const dParts = propDate.split(" - ");
    date1 = dParts[0] || "N/A";
    date2 = dParts[1] || date1;
    const tParts = propTime?.split(" - ") || [];
    time1 = tParts[0] || "N/A";
    time2 = tParts[1] || "N/A";
  }

  return {
    dateDisplay: isTwoDates ? `${date1} \u2192 ${date2}` : date1,
    timeDisplay: `${time1} \u2192 ${time2}`,
  };
};

// Transport Images Mapping
import animalTransportImg from "../../assets/images/driver_transport_icon/animal_transport.png";
import bicycleImg from "../../assets/images/driver_transport_icon/bicycle.png";
import buildingMaterialsImg from "../../assets/images/driver_transport_icon/building_materials.png";
import carImg from "../../assets/images/driver_transport_icon/car.png";
import documentImg from "../../assets/images/driver_transport_icon/document.png";
import electricalImg from "../../assets/images/driver_transport_icon/electrical.png";
import furnitureImg from "../../assets/images/driver_transport_icon/furniture.png";
import motorcycleImg from "../../assets/images/driver_transport_icon/motorcycle.png";
import movingImg from "../../assets/images/driver_transport_icon/moving.png";
import otherImg from "../../assets/images/driver_transport_icon/other.png";
import paketeImg from "../../assets/images/driver_transport_icon/pakete.png";
import paletteImg from "../../assets/images/driver_transport_icon/palette.png";
import refrigeratorImg from "../../assets/images/driver_transport_icon/refrigerator.png";
import fastImg from "../../assets/images/driver_transport_icon/rocket.png";
import tiresImg from "../../assets/images/driver_transport_icon/tires.png";

const transportImages = {
  palette: paletteImg,
  pakete: paketeImg,
  document: documentImg,
  furniture: furnitureImg,
  electrical: electricalImg,
  tires: tiresImg,
  refrigerator: refrigeratorImg,
  "building materials": buildingMaterialsImg,
  moving: movingImg,
  bicycle: bicycleImg,
  motorcycle: motorcycleImg,
  car: carImg,
  "animal transport": animalTransportImg,
  other: otherImg,
  fast: fastImg,
  regular: paketeImg,
  furnitures: furnitureImg,
  cars: carImg,
};

const TransportImage = ({ type, item, className = "", size = 28 }) => {
  const getImageSrc = () => {
    const booking = item.booking || item.transport || item || {};
    let transportType = (
      booking.transport_type ||
      item.transport_type ||
      item.type ||
      booking.type ||
      ""
    ).toLowerCase();

    // Fallback to fast_order or shipment_data if transportType is empty
    if (!transportType) {
      if (booking.shipment_data?.[0]?.shipment_data) {
        transportType = booking.shipment_data[0].shipment_data.toLowerCase();
      } else if (booking.fast_order !== undefined) {
        transportType = booking.fast_order === 1 ? "fast" : "regular";
      }
    }

    // Mapping to match the transportImages keys
    let mappedType = transportType;
    if (transportType === "furnitures") mappedType = "furniture";
    if (transportType === "cars") mappedType = "car";
    if (transportType === "pakete" || transportType === "regular") mappedType = "pakete";
    if (transportType === "pallete" || transportType === "palette") mappedType = "palette";

    return (
      transportImages[mappedType] ||
      transportImages[transportType] ||
      transportImages.other
    );
  };

  return (
    <div
      style={{
        width: `${size + 8}px`,
        height: `${size + 8}px`,
        backgroundColor: "#fff",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <img
        src={getImageSrc()}
        alt="transport"
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
        }}
        onError={(e) => {
          e.target.src = otherImg;
        }}
      />
    </div>
  );
};

const OrderDetailsView = ({ item, isCompleted, onReceive, onComplete }) => {
  const { t } = useTranslation();
  const orderData = item.booking || item.transport || {};
  const bookingId = item.order_id || orderData.booking_id || item.id;

  const parseCoordinates = (coordString) => {
    if (!coordString) return null;
    try {
      const [lat, lng] = coordString
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      return [lat, lng];
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  const fromCoords = useMemo(
    () => parseCoordinates(orderData.booking_from_lat_long),
    [orderData.booking_from_lat_long],
  );
  const toCoords = useMemo(
    () => parseCoordinates(orderData.booking_to_lat_long),
    [orderData.booking_to_lat_long],
  );

  return (
    <div className="p-3">
      <Row className="g-4">
        {/* Column 1: Route */}
        <Col xs={12} lg={4}>
          <Card
            className="h-100 dark-bg-dark border-0 shadow-sm"
            style={{ backgroundColor: "#1e1e1e", borderRadius: "12px" }}
          >
            <Card.Header className="bg-transparent border-0 pt-3 pb-0">
              <h6 className="mb-0 text-orange fw-bold">
                <FaMapMarkerAlt className="me-2" />
                {t("driverCard.general.route")}
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div
                style={{
                  height: "300px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #333",
                }}
              >
                {fromCoords && toCoords ? (
                  <RouteMap fromCoords={fromCoords} toCoords={toCoords} />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted small">
                    {t("driverCard.general.invalidCoords")}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Column 2: Fahrzeug & Aktionen */}
        <Col xs={12} lg={4}>
          <Card
            className="h-100 dark-bg-dark border-0 shadow-sm"
            style={{ backgroundColor: "#1e1e1e", borderRadius: "12px" }}
          >
            <Card.Header className="bg-transparent border-0 pt-3 pb-0">
              <h6 className="mb-0 text-orange fw-bold">
                <FaCar className="me-2" />
                {t("driverCard.orderDetails.vehicleAndActions")}
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {/* Abholung Sections - all stages */}
              {(Array.isArray(orderData.booking_from)
                ? orderData.booking_from
                : [orderData.booking_from].filter(Boolean)
              ).map((_, i) => (
                <div className="mb-4" key={`pickup-${i}`}>
                  <h6 className="text-orange fw-bold mb-3 small">
                    {t("driverCard.general.pickup")} ({i + 1})
                  </h6>
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: "#121212",
                      border: "1px solid #2a2a2a",
                    }}
                  >
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.company")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_company?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.name")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_first_name?.[i]}{" "}
                        {orderData.loading_last_name?.[i]}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.address")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_streat_no?.[i] ||
                          orderData.booking_from?.[i]}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.additional_address")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_address_additional?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.phone")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_phone?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">Datum:</span>{" "}
                      <span className="text-white">
                        {orderData.loading_date_time?.[i] || "-"}
                      </span>
                    </div>
                    <div className="small">
                      <span className="text-muted">
                        {t("clientDetails.pickup.reference")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.loading_reffernce?.[i] || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Zustellung Sections - all stages */}
              {(Array.isArray(orderData.booking_to)
                ? orderData.booking_to
                : [orderData.booking_to].filter(Boolean)
              ).map((_, i) => (
                <div key={`delivery-${i}`} className={i > 0 ? "mt-4" : ""}>
                  <h6 className="text-orange fw-bold mb-3 small">
                    {t("driverCard.general.delivery")} ({i + 1})
                  </h6>
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: "#121212",
                      border: "1px solid #2a2a2a",
                    }}
                  >
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.company")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_company?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.name")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_first_name?.[i]}{" "}
                        {orderData.unloading_last_name?.[i]}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.address")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_streat_no?.[i] ||
                          orderData.booking_to?.[i]}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.additional_address")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_address_additional?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.phone")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_phone?.[i] || "-"}
                      </span>
                    </div>
                    <div className="mb-2 small">
                      <span className="text-muted">Datum:</span>{" "}
                      <span className="text-white">
                        {orderData.unloading_date_time?.[i] || "-"}
                      </span>
                    </div>
                    <div className="small">
                      <span className="text-muted">
                        {t("clientDetails.delivery.reference")}:
                      </span>{" "}
                      <span className="text-white">
                        {orderData.unloading_reffernce?.[i] || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Action Buttons */}
              {!isCompleted &&
                (() => {
                  const stagesList = Array.isArray(orderData.booking_from)
                    ? orderData.booking_from
                    : [orderData.booking_from].filter(Boolean);

                  const toList = Array.isArray(orderData.booking_to)
                    ? orderData.booking_to
                    : [orderData.booking_to].filter(Boolean);

                  const count = Math.max(stagesList.length, toList.length);
                  const steps = Array.from({ length: count });

                  return steps.map((_, idx) => (
                    <div
                      key={`stage-action-${idx}`}
                      className="d-flex flex-column mt-4 p-3 rounded"
                      style={{
                        backgroundColor: "#1a1a1a",
                        border: "1px solid #2a2a2a",
                      }}
                    >
                      <h6 className="text-white fw-bold mb-2">
                        Auftragsphase {idx + 1}
                      </h6>
                      <div className="mb-3">
                        <span
                          style={{
                            backgroundColor: "#00d2ff",
                            color: "#fff",
                            padding: "4px 12px",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                            display: "inline-block",
                          }}
                        >
                          {stagesList[idx] || "-"} → {toList[idx] || "-"}
                        </span>
                      </div>

                      <div className="d-flex flex-column gap-2">
                        {onReceive && (
                          <Button
                            className="bg-orange text-white w-100 fw-bold py-2 shadow-sm border-0"
                            style={{ borderRadius: "8px", fontSize: "14px" }}
                            onClick={() => onReceive(idx + 1)}
                          >
                            <FaUpload className="me-2 mb-1" />
                            {t(
                              "driverRequest.buttons.receive",
                              `Abholung bestätigen ${idx + 1}`,
                            )}
                          </Button>
                        )}
                        {onComplete && (
                          <Button
                            variant="success"
                            className="w-100 fw-bold py-2 shadow-sm border-0"
                            style={{
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: "#1e7e34",
                            }}
                            onClick={() => onComplete(idx + 1)}
                          >
                            <FaDownload className="me-2 mb-1" />
                            {t(
                              "driverRequest.buttons.complete",
                              `Zustellung bestätigen ${idx + 1}`,
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ));
                })()}

              {/* CMR List */}
              {(() => {
                const bills = item.bill_path || orderData.bill_path;
                if (!Array.isArray(bills)) return null;
                return bills.map((billUrl, index) => (
                  <div key={`cmr-${index}`} className="my-3">
                    <Button
                      variant="primary"
                      className="w-100 py-2 fw-bold border-0 shadow-sm"
                      onClick={() => window.open(billUrl, "_blank")}
                      style={{ borderRadius: "8px", fontSize: "14px" }}
                    >
                      <FaFileInvoice className="me-2 mb-1" />
                      CMR {index + 1}
                    </Button>
                  </div>
                ));
              })()}
            </Card.Body>
          </Card>
        </Col>

        {/* Column 3: Sendungsdetails */}
        <Col xs={12} lg={4}>
          <Card
            className="h-100 dark-bg-dark border-0 shadow-sm"
            style={{ backgroundColor: "#1e1e1e", borderRadius: "12px" }}
          >
            <Card.Header className="bg-transparent border-0 pt-3 pb-0">
              <h6 className="mb-0 text-orange fw-bold">
                <FaBox className="me-2" />
                {t("driverCard.orderDetails.shipmentDetails")}
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {/* Packsücke section */}
              <div className="mb-4">
                <h6 className="text-white fw-bold mb-3 small">
                  {t("driverCard.general.items")}
                </h6>
                <div
                  className="p-3 rounded d-flex align-items-center"
                  style={{
                    backgroundColor: "#121212",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <FaTruck className="me-3 text-muted" size={20} />
                  <div>
                    <div className="text-white fw-bold small">
                      {orderData.vehicle_type || item.vehicle_type}
                    </div>
                    <div className="text-muted extra-small">
                      {t("driverCard.orderDetails.vehicleType")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sendung section */}
              <div className="mb-4">
                <h6 className="text-white fw-bold mb-3 small">
                  {t("driverCard.orderDetails.shipment")} (1)
                </h6>
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: "#121212",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  {orderData.shipment_data?.map((s, idx) => (
                    <div
                      key={idx}
                      className={
                        idx > 0 ? "mt-3 pt-3 border-top border-secondary" : ""
                      }
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Badge bg="orange" style={{ fontSize: "10px" }}>
                          #{idx + 1}
                        </Badge>
                        <span className="text-white fw-bold small">
                          {s.quantity || 1} × {s.shipment_data}
                        </span>
                      </div>
                      <div className="text-muted small mb-1">
                        <FaRulerCombined className="me-2" /> {s.length} ×{" "}
                        {s.width} × {s.height} cm
                      </div>
                      <div className="text-white small fw-bold">
                        <FaWeightHanging className="me-2 text-muted" />{" "}
                        {t("driverCard.transportDetails.totalWeight")}:{" "}
                        {s.weight} kg
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Weight */}
              <div className="d-flex justify-content-between align-items-center mt-auto">
                <span className="text-white fw-bold small">
                  {t("driverCard.transportDetails.totalWeight")}:
                </span>
                <Badge
                  bg="success"
                  className="px-3 py-2"
                  style={{ borderRadius: "4px" }}
                >
                  {orderData.total_weight || item.weight} kg
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
const getTypeText = (type, item, t) => {
  const booking = item.booking || item.transport || item || {};
  let transportType = (
    booking.transport_type ||
    item.transport_type ||
    item.type ||
    booking.type ||
    ""
  ).toLowerCase();

  // Fallback to shipment_data or fast_order
  if (!transportType) {
    if (booking.shipment_data?.[0]?.shipment_data) {
      transportType = booking.shipment_data[0].shipment_data.toLowerCase();
    } else if (booking.fast_order !== undefined) {
      transportType = booking.fast_order === 1 ? "fast" : "regular";
    }
  }

  // Handle specific mappings
  if (transportType === "pallete" || transportType === "palette") {
    return t("driverRequest.orderTypes.palette") || "Palette";
  }

  switch (transportType) {
    case "fast":
      return t("driverRequest.orderTypes.fast") || "Direktfahrt";
    case "regular":
      return t("driverRequest.orderTypes.regular") || "Beiladung";
    case "furnitures":
    case "furniture":
      return t("driverRequest.orderTypes.furniture") || "Möbel";
    case "cars":
    case "car":
      return t("driverRequest.orderTypes.car") || "Fahrzeug";
    default:
      return transportType
        ? transportType.charAt(0).toUpperCase() + transportType.slice(1)
        : t("driverRequest.orderTypes.regular") || "Beiladung";
  }
};

export default function DriverCompanyOrders({ type }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [offCanvasOpen, setOffCanvasOpen] = useState(null);

  // States for receive/complete modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStageNumber, setSelectedStageNumber] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [receiveDate, setReceiveDate] = useState("");
  const [deliverDate, setDeliverDate] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const isCompleted = type === "completed";
  const title = isCompleted
    ? t("driverCompleted.title") || "Completed Orders"
    : t("driverActive.title") || "Active Orders";

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const responses = isCompleted
        ? await Promise.all([
            driverAPI.bookingsOrders.regularCompleted(),
            driverAPI.transportsOrders.furnitureCompleted(),
            driverAPI.transportsOrders.carCompleted(),
          ])
        : await Promise.all([
            driverAPI.bookingsOrders.regularAccepted(),
            driverAPI.transportsOrders.furnitureAccepted(),
            driverAPI.transportsOrders.carAccepted(),
          ]);

      console.log("!!!! API Responses:", responses);

      const allOrders = responses.flatMap((response) => {
        if (!response?.success) {
          return [];
        }

        return response.data?.data || [];
      });

      console.log("!!!! All Orders:", allOrders);

      setData(allOrders);

    } catch (err) {
      console.error("Fetch orders error:", err);

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isCompleted, type]);

  const openReceiveConfirm = (item, stageNumber) => {
    setSelectedItem(item);
    setSelectedStageNumber(stageNumber);
    setShowReceiveModal(true);
    setReceiveDate("");
  };

  const openDeliverConfirm = (item, stageNumber) => {
    setSelectedItem(item);
    setSelectedStageNumber(stageNumber);
    setShowDeliverModal(true);
    setDeliverDate("");
  };
  const handleReceiveOrder = async () => {
    if (!selectedItem) return;
    try {
      const orderId = selectedItem.id;
      const isBooking = !!selectedItem.booking;
      const payload = {
        received_date: receiveDate,
        stage_number: selectedStageNumber,
      };

      const apiCall = isBooking
        ? driverAPI.bookingsOrders.receive(orderId, payload)
        : driverAPI.transportsOrders.receive(orderId, payload);

      const resp = await apiCall;
      if (resp.success) {
        setShowReceiveModal(false);
        Swal.fire({
          icon: "success",
          title: t("driverRequest.alerts.success"),
          text: t("driverRequest.alerts.receiveSuccess"),
        });
        fetchData();
      } else {
        Swal.fire({
          icon: "error",
          title: t("driverRequest.alerts.error"),
          text: resp.message || "Failed to receive order.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("driverRequest.alerts.error"),
        text: translateBackendMessage(
          err?.response?.data || err,
          "Error receiving order.",
        ),
      });
      console.error(err);
    }
  };

  const handleCompleteOrder = async () => {
    if (!selectedItem) return;
    try {
      const orderId = selectedItem.id;
      const isBooking = !!selectedItem.booking;

      const payload = {
        completed_date: deliverDate,
        stage_number: selectedStageNumber,
      };

      const apiCall = isBooking
        ? driverAPI.bookingsOrders.complete(orderId, payload)
        : driverAPI.transportsOrders.complete(orderId, payload);

      const resp = await apiCall;
      if (resp.success) {
        setShowDeliverModal(false);
        setShowSuccess(true);
        fetchData();
      } else {
        Swal.fire({
          icon: "error",
          title: t("driverRequest.alerts.error"),
          text: resp.message || "Failed to complete order.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("driverRequest.alerts.error"),
        text: translateBackendMessage(
          err?.response?.data || err,
          "Error completing order.",
        ),
      });
      console.error(err);
    }
  };

  const toggleOffCanvas = (index) => {
    setOffCanvasOpen(offCanvasOpen === index ? null : index);
  };

  return (
    <>
      {/* fu knj  */}
      <NavbarMenu />
      <Container
        fluid
        className="p-4 dark-bg-darker"
        style={{ minHeight: "100vh" }}
      >
        <h2 className="text-white mb-4 text-center">{title}</h2>
        {error && (
          <Alert variant="danger" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {loading ? (
          <p className="text-center text-white">
            {t("common.loading") || "Loading..."}
          </p>
        ) : data.length === 0 ? (
          <p className="text-center text-white">
            {t("driverCompleted.general.noData") || "No orders found."}
          </p>
        ) : (
          <>
            {/* Desktop View */}
            <Accordion
              className="d-none d-md-block dark-accordion-container"
              activeKey={activeKey}
              onSelect={(k) => setActiveKey(k)}
            >
              {data.map((item, index) => {
                const orderData = item.booking || item.transport || item || {};
                // const bookingId = item.order_id || orderData.booking_id || item.id;
                const bookingId = orderData.booking_id || item.id;
                const pickupAddress =
                  (Array.isArray(orderData.booking_from)
                    ? orderData.booking_from[0]
                    : orderData.booking_from) ||
                  orderData.pickup_address ||
                  "N/A";
                const deliveryAddress =
                  (Array.isArray(orderData.booking_to)
                    ? orderData.booking_to[0]
                    : orderData.booking_to) ||
                  orderData.delivery_address ||
                  "N/A";

                return (
                  <Accordion.Item
                    eventKey={index.toString()}
                    key={item.id || index}
                    style={{
                      backgroundColor: "#1e1e1e",
                      border: "1px solid #333",
                      borderRadius: "12px",
                      marginBottom: "1rem",
                      overflow: "hidden",
                    }}
                  >
                    <Accordion.Header>
                      <div className="w-100 p-2">
                        {/* Header Top: Driver Company */}
                        <div
                          className="d-flex align-items-center mb-3 pb-3"
                          style={{ borderBottom: "1px solid #333" }}
                        >
                          <TransportImage type={type} item={item} size={32} />
                          <h6 className="mb-0 ms-3 text-white fw-bold">
                            <span className="text-muted pe-2 small">
                              Auftrag: {bookingId}
                            </span>
                            | {getTypeText(type, item, t)}
                          </h6>
                          <Badge
                            bg="secondary"
                            className="ms-auto text-white px-3 py-1"
                            style={{ borderRadius: "20px", fontSize: "0.8rem", backgroundColor: "#333" }}
                          >
                            {Math.max(
                              Array.isArray(orderData.booking_from)
                                ? orderData.booking_from.length
                                : 1,
                              Array.isArray(orderData.booking_to)
                                ? orderData.booking_to.length
                                : 1,
                            )}{" "}
                            {t("driverCard.general.stopsCount", "Stops")}
                          </Badge>
                        </div>

                        {/* Middle Content */}
                        <Row className="align-items-center g-2 px-2">
                          {/* Pickup */}
                          <Col xs={12} md={3}>
                            <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                              {t(
                                "driverCard.general.pickupTitle",
                                "ABHOLUNG (BELADUNG)",
                              )}
                            </h6>
                            {(Array.isArray(orderData.booking_from)
                              ? orderData.booking_from
                              : [orderData.booking_from].filter(Boolean)
                            ).map((addr, i) => {
                              const dtStr =
                                orderData.loading_date_time?.[i] || "";
                              const spaceIdx = dtStr.indexOf(" ");
                              const dtDate =
                                spaceIdx > -1
                                  ? dtStr.substring(0, spaceIdx)
                                  : dtStr;
                              const dtTime =
                                spaceIdx > -1
                                  ? dtStr
                                    .substring(spaceIdx + 1)
                                    .replace(" - ", " → ")
                                  : "-";
                              return (
                                <div key={i} className="mb-2">
                                  <div className="fw-bold text-orange mb-1 small">
                                    {t("driverCard.general.pickup")} ({i + 1})
                                  </div>
                                  <div className="mb-1 d-flex align-items-center text-white fw-bold">
                                    <FaMapMarkerAlt className="text-orange me-2 fs-5 flex-shrink-0" />
                                    <span>{addr}</span>
                                  </div>
                                  <div
                                    className="p-2 d-inline-block"
                                    style={{
                                      backgroundColor: "inherit",
                                      borderRadius: "8px",
                                      border: "1px solid #333",
                                    }}
                                  >
                                    <div className="d-flex align-items-center mb-1 gap-3">
                                      <span
                                        className="d-flex align-items-center text-muted small"
                                        style={{ minWidth: "70px" }}
                                      >
                                        <FaCalendarAlt className="text-orange me-2" />{" "}
                                        {t("driverCard.general.dates", "Datum")}
                                      </span>
                                      <span className="text-white fw-medium small flex-grow-1 text-start">
                                        {dtDate || "-"}
                                      </span>
                                    </div>
                                    <div
                                      className="d-flex align-items-center mt-1 pt-1 gap-3"
                                      style={{ borderTop: "1px solid #333" }}
                                    >
                                      <span
                                        className="d-flex align-items-center text-muted small"
                                        style={{ minWidth: "70px" }}
                                      >
                                        <AlarmIcon
                                          className="text-orange me-2"
                                          style={{ width: 16 }}
                                        />{" "}
                                        <span style={{ marginLeft: "4px" }}>
                                          {t(
                                            "driverCard.general.time",
                                            "Uhrzeit",
                                          )}
                                        </span>
                                      </span>
                                      <span className="text-white fw-medium small flex-grow-1 text-start">
                                        {dtTime || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </Col>

                          {/* Arrow */}
                          <Col
                            xs={12}
                            md={3}
                            className="text-start d-none d-md-flex p-0"
                            style={{ position: "relative", minHeight: "100px" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-start"
                              style={{
                                position: "absolute",
                                top: "70%",
                                left: "0%",
                                transform: "translate(0%, -50%)",
                                minWidth: "80px",
                                width: "60%",
                                zIndex: 1,
                              }}
                            >
                              <div
                                style={{
                                  height: "1px",
                                  backgroundColor: "#6c757d",
                                  flex: 1,
                                }}
                              ></div>
                              <div
                                style={{
                                  width: "0",
                                  height: "0",
                                  borderTop: "4px solid transparent",
                                  borderBottom: "4px solid transparent",
                                  borderLeft: "6px solid #6c757d",
                                }}
                              ></div>
                            </div>
                          </Col>

                          {/* Delivery */}
                          <Col xs={12} md={4}>
                            <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                              {t(
                                "driverCard.general.deliveryTitle",
                                "ZUSTELLUNG (ENTLADUNG)",
                              )}
                            </h6>
                            {(Array.isArray(orderData.booking_to)
                              ? orderData.booking_to
                              : [orderData.booking_to].filter(Boolean)
                            ).map((addr, i) => {
                              const dtStr =
                                orderData.unloading_date_time?.[i] || "";
                              const spaceIdx = dtStr.indexOf(" ");
                              const dtDate =
                                spaceIdx > -1
                                  ? dtStr.substring(0, spaceIdx)
                                  : dtStr;
                              const dtTime =
                                spaceIdx > -1
                                  ? dtStr
                                    .substring(spaceIdx + 1)
                                    .replace(" - ", " → ")
                                  : "-";
                              return (
                                <div key={i} className="mb-2">
                                  <div className="fw-bold text-orange mb-1 small">
                                    {t("driverCard.general.delivery")} ({i + 1})
                                  </div>
                                  <div className="mb-1 d-flex align-items-center text-white fw-bold">
                                    <FaMapMarkerAlt className="text-secondary me-2 fs-5 flex-shrink-0" />
                                    <span>{addr}</span>
                                  </div>
                                  <div
                                    className="p-2 d-inline-block"
                                    style={{
                                      backgroundColor: "inherit",
                                      borderRadius: "8px",
                                      border: "1px solid #333",
                                    }}
                                  >
                                    <div className="d-flex align-items-center mb-1 gap-3">
                                      <span
                                        className="d-flex align-items-center text-muted small"
                                        style={{ minWidth: "70px" }}
                                      >
                                        <FaCalendarAlt className="text-orange me-2" />{" "}
                                        {t("driverCard.general.dates", "Datum")}
                                      </span>
                                      <span className="text-white fw-medium small flex-grow-1 text-start">
                                        {dtDate || "-"}
                                      </span>
                                    </div>
                                    <div
                                      className="d-flex align-items-center mt-1 pt-1 gap-3"
                                      style={{ borderTop: "1px solid #333" }}
                                    >
                                      <span
                                        className="d-flex align-items-center text-muted small"
                                        style={{ minWidth: "70px" }}
                                      >
                                        <AlarmIcon
                                          className="text-orange me-2"
                                          style={{ width: 16 }}
                                        />{" "}

                                        <span style={{ marginLeft: "4px" }}>

                                          {t(
                                            "driverCard.general.time",
                                            "Uhrzeit",
                                          )}
                                        </span>
                                      </span>
                                      <span className="text-white fw-medium small flex-grow-1 text-start">
                                        {dtTime || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </Col>

                          {/* Stats */}
                          <Col xs={12} md={2}>
                            <div
                              className="ps-md-2 d-flex flex-column align-items-center text-center"
                              style={{ borderLeft: "1px solid #333", height: "100%", justifyContent: "center" }}
                            >
                              <div
                                className="mb-2 pb-2 w-100 d-flex flex-column align-items-center"
                                style={{ borderBottom: "1px solid #333" }}
                              >
                                <div className="text-muted d-flex align-items-center mb-1">
                                  <FaRoute className="me-2 text-secondary" />{" "}
                                  {t("driverCard.general.route", "Route:")}
                                </div>
                                <div className="text-white fw-bold fs-5">
                                  {orderData.distance || "N/A"}
                                </div>
                              </div>
                              <div className="w-100 d-flex flex-column align-items-center">
                                <div className="text-muted d-flex align-items-center mb-1">
                                  <FaWeightHanging className="me-2 text-secondary" />{" "}
                                  {t("driverCard.general.weight", "Gewicht:")}
                                </div>
                                <div className="text-white fw-bold fs-5">
                                  {formatNumber(
                                    orderData.total_weight || item.weight || 0,
                                  )}{" "}
                                  Kg
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div
                          className="d-flex justify-content-end mt-3 pt-2 pe-3"
                          style={{ borderTop: "1px solid #333" }}
                        >
                          <div
                            className={`dark-artikel-collapse-icon ${activeKey === index.toString() ? "open" : ""}`}
                          >
                            <FaChevronDown style={{ color: "#ff7b00" }} />
                          </div>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div
                        className="dark-bg-darker p-3"
                        style={{ borderRadius: "12px" }}
                      >
                        <OrderDetailsView
                          item={item}
                          isCompleted={isCompleted}
                          onReceive={(stage) => openReceiveConfirm(item, stage)}
                          onComplete={(stage) =>
                            openDeliverConfirm(item, stage)
                          }
                        />
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>

            {/* Mobile View */}
            <div className="d-block d-md-none">
              {data.map((item, index) => {
                const orderData = item.booking || item.transport || {};
                const bookingId =
                  item.order_id || orderData.booking_id || item.id;
                const pickupAddress =
                  (Array.isArray(orderData.booking_from)
                    ? orderData.booking_from[0]
                    : orderData.booking_from) ||
                  orderData.pickup_address ||
                  "N/A";
                const deliveryAddress =
                  (Array.isArray(orderData.booking_to)
                    ? orderData.booking_to[0]
                    : orderData.booking_to) ||
                  orderData.delivery_address ||
                  "N/A";

                return (
                  <div
                    key={item.id || index}
                    className="dark-mobile-card"
                    style={{
                      backgroundColor: "#1e1e1e",
                      border: "1px solid #333",
                      borderRadius: "12px",
                      marginBottom: "1rem",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="p-3 pb-2"
                      style={{ borderBottom: "1px solid #333" }}
                    >
                      <div className="d-flex align-items-center">
                        <TransportImage type={type} item={item} size={32} />
                        <h5 className="mb-0 ms-3 text-white fw-bold">
                          <span className="text-muted pe-1 small" style={{ fontSize: "0.8rem" }}>
                            Auftrag: {bookingId}
                          </span>
                          | {getTypeText(type, item, t)}
                        </h5>
                        <Badge
                          bg="secondary"
                          className="ms-auto text-white px-3 py-1"
                          style={{ borderRadius: "20px", fontSize: "0.8rem", backgroundColor: "#333" }}
                        >
                          {Math.max(
                            Array.isArray(orderData.booking_from)
                              ? orderData.booking_from.length
                              : 1,
                            Array.isArray(orderData.booking_to)
                              ? orderData.booking_to.length
                              : 1,
                          )}{" "}
                          {t("driverCard.general.stopsCount", "Stops")}
                        </Badge>
                      </div>
                    </div>

                    <div className="p-3">
                      {/* Pickup Section */}
                      <div className="d-flex mb-3">
                        <div className="me-3 mt-1">
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "50%",
                              border: "1px solid #ff7b00",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaUpload style={{ color: "#ff7b00" }} />
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="text-orange fw-bold mb-1 small text-uppercase">
                            {t(
                              "driverCard.general.pickupTitle",
                              "ABHOLUNG (BELADUNG)",
                            )}
                          </h6>
                          {(Array.isArray(orderData.booking_from)
                            ? orderData.booking_from
                            : [orderData.booking_from].filter(Boolean)
                          ).map((addr, i) => {
                            const dtStr =
                              orderData.loading_date_time?.[i] || "";
                            const spaceIdx = dtStr.indexOf(" ");
                            const dtDate =
                              spaceIdx > -1
                                ? dtStr.substring(0, spaceIdx)
                                : dtStr;
                            const dtTime =
                              spaceIdx > -1
                                ? dtStr
                                  .substring(spaceIdx + 1)
                                  .replace(" - ", " → ")
                                : "-";
                            return (
                              <div key={i} className="mb-2">
                                <div className="fw-bold text-orange mb-1 extra-small">
                                  {t("driverCard.general.pickup")} ({i + 1})
                                </div>
                                <div className="mb-1 d-flex align-items-center text-white fw-bold">
                                  <FaMapMarkerAlt className="text-orange me-2 flex-shrink-0" />
                                  <span>{addr}</span>
                                </div>
                                <div
                                  className="p-2"
                                  style={{
                                    backgroundColor: "inherit",
                                    borderRadius: "8px",
                                    border: "1px solid #333",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className="d-flex align-items-center text-muted small">
                                      <FaCalendarAlt className="text-orange me-2" />{" "}
                                      {t("driverCard.general.dates", "Datum")}
                                    </span>
                                    <span className="text-white fw-medium small">
                                      {dtDate || "-"}
                                    </span>
                                  </div>
                                  <div
                                    className="d-flex align-items-center gap-2 mt-1 pt-1"
                                    style={{ borderTop: "1px solid #333" }}
                                  >
                                    <span className="d-flex align-items-center text-muted small">
                                      <AlarmIcon
                                        className="text-orange me-2"
                                        style={{ width: 16 }}
                                      />{" "}
                                      <span style={{ marginLeft: "4px" }}>

                                        {t("driverCard.general.time", "Uhrzeit")}
                                      </span>
                                    </span>
                                    <span className="text-white fw-medium small">
                                      {dtTime || "-"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Delivery Section */}
                      <div className="d-flex mb-3">
                        <div className="me-3 mt-1">
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "50%",
                              border: "1px solid #ff7b00",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaDownload style={{ color: "#ff7b00" }} />
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="text-orange fw-bold mb-1 small text-uppercase">
                            {t(
                              "driverCard.general.deliveryTitle",
                              "ZUSTELLUNG (ENTLADUNG)",
                            )}
                          </h6>
                          {(Array.isArray(orderData.booking_to)
                            ? orderData.booking_to
                            : [orderData.booking_to].filter(Boolean)
                          ).map((addr, i) => {
                            const dtStr =
                              orderData.unloading_date_time?.[i] || "";
                            const spaceIdx = dtStr.indexOf(" ");
                            const dtDate =
                              spaceIdx > -1
                                ? dtStr.substring(0, spaceIdx)
                                : dtStr;
                            const dtTime =
                              spaceIdx > -1
                                ? dtStr
                                  .substring(spaceIdx + 1)
                                  .replace(" - ", " → ")
                                : "-";
                            return (
                              <div key={i} className="mb-2">
                                <div className="fw-bold text-orange mb-1 extra-small">
                                  {t("driverCard.general.delivery")} ({i + 1})
                                </div>
                                <div className="mb-1 d-flex align-items-center text-white fw-bold">
                                  <FaMapMarkerAlt className="text-secondary me-2 flex-shrink-0" />
                                  <span>{addr}</span>
                                </div>
                                <div
                                  className="p-2"
                                  style={{
                                    backgroundColor: "inherit",
                                    borderRadius: "8px",
                                    border: "1px solid #333",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className="d-flex align-items-center text-muted small">
                                      <FaCalendarAlt className="text-orange me-2" />{" "}
                                      {t("driverCard.general.dates", "Datum")}
                                    </span>
                                    <span className="text-white fw-medium small">
                                      {dtDate || "-"}
                                    </span>
                                  </div>
                                  <div
                                    className="d-flex align-items-center gap-2 mt-1 pt-1"
                                    style={{ borderTop: "1px solid #333" }}
                                  >
                                    <span className="d-flex align-items-center text-muted small">
                                      <AlarmIcon
                                        className="text-orange me-2"
                                        style={{ width: 16 }}
                                      />{" "}
                                      <span style={{ marginLeft: "4px" }}>

                                        {t("driverCard.general.time", "Uhrzeit")}
                                      </span>
                                    </span>
                                    <span className="text-white fw-medium small">
                                      {dtTime || "-"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Stats Section */}
                      <div
                        className="d-flex text-center py-2 mt-3"
                        style={{
                          borderTop: "1px solid #333",
                          borderBottom: "1px solid #333",
                        }}
                      >
                        <div
                          className="flex-fill d-flex flex-column align-items-center justify-content-center"
                          style={{ borderRight: "1px solid #333" }}
                        >
                          <FaRoute className="text-white fs-4 rounded-circle mb-1" />
                          <div className="text-muted small">
                            {t("driverCard.general.route", "Route:")}
                          </div>
                          <div className="text-white fw-bold">
                            {orderData.distance || "N/A"}
                          </div>
                        </div>
                        <div
                          className="flex-fill d-flex flex-column align-items-center justify-content-center"
                          style={{ borderRight: "1px solid #333" }}
                        >
                          <FaWeightHanging className="text-white fs-4 rounded-circle mb-1" />
                          <div className="text-muted small">
                            {t(
                              "driverCard.transportDetails.totalWeight",
                              "Weight:",
                            )}
                          </div>
                          <div className="text-white fw-bold">
                            {formatNumber(
                              orderData.total_weight || item.weight,
                            )}{" "}
                            Kg
                          </div>
                        </div>

                      </div>

                      {/* Footer Actions */}
                      <div className="mt-3">
                        <button
                          className="btn btn-outline-warning rounded-pill fw-bold px-4 py-2"
                          onClick={() => toggleOffCanvas(index)}
                          style={{ borderColor: "#ff7b00", color: "#ff7b00" }}
                        >
                          {t(
                            "driverRequest.buttons.details",
                            "Details anzeigen",
                          )}{" "}
                          <FaChevronDown className="ms-2" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile Drawer (Offcanvas) */}
            {data.map((item, index) => (
              <div key={`drawer-${index}`}>
                <div
                  className={`offcanvas-overlay ${offCanvasOpen === index ? "show" : ""}`}
                  onClick={() => setOffCanvasOpen(null)}
                />
                <div
                  className={`offcanvas-accordion-body ${offCanvasOpen === index ? "open" : ""}`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                      className="offcanvas-close-btn position-relative m-0"
                      onClick={() => setOffCanvasOpen(null)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <Card className="offcanvas-card border-0 bg-transparent">
                    <Card.Body className="p-0">
                      <OrderDetailsView
                        item={item}
                        isCompleted={isCompleted}
                        onReceive={(stage) => openReceiveConfirm(item, stage)}
                        onComplete={(stage) => openDeliverConfirm(item, stage)}
                      />
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ))}
          </>
        )}

        <Modal
          show={showReceiveModal}
          onHide={() => {
            setShowReceiveModal(false);
            setError(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("driverRequest.modals.receiveDate")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t("driverRequest.modals.receiveDate")}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={receiveDate}
                onChange={(e) => setReceiveDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowReceiveModal(false);
                setError(null);
              }}
            >
              {t("driverRequest.buttons.cancel")}
            </Button>
            <Button className="bg-orange" onClick={handleReceiveOrder}>
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showDeliverModal}
          onHide={() => {
            setShowDeliverModal(false);
            setError(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("driverRequest.modals.confirmDelivery")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {t("driverRequest.modals.receiveDate")}: {receiveDate}
            </p>
            <Form.Group>
              <Form.Label>{t("driverRequest.modals.deliveryDate")}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={deliverDate}
                onChange={(e) => setDeliverDate(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeliverModal(false);
                setError(null);
              }}
            >
              {t("driverRequest.buttons.cancel")}
            </Button>
            <Button className="bg-orange" onClick={handleCompleteOrder}>
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showSuccess}
          onHide={() => setShowSuccess(false)}
          centered
          backdrop="static"
        >
          <Modal.Body className="text-center p-4">
            <FaCheckCircle size={60} className="text-success mb-3" />
            <h5 className="fw-bold mb-3">
              {t("driverRequest.alerts.successfullyCompleted")}
            </h5>
            <Button
              variant="success"
              onClick={() => {
                setShowSuccess(false);
              }}
            >
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
}
